import { ProductRecogniseImageInput } from 'api/schemas'
import { ApolloQueryResult } from '@apollo/client'
import { ls } from 'utils/my-localstorage'

/**
 * Validates required bottle image fields
 */
export const validateRequiredFields = (
  bottleImages: any,
  setError: (path: string, error: { type: string; message: string }) => void,
  errorMessage: string
): boolean => {
  const requiredFields = [
    { field: 'frontLabel', path: 'bottleImages.frontLabel' },
    { field: 'backLabel', path: 'bottleImages.backLabel' },
  ]

  const hasErrors = requiredFields.some(({ field, path }) => {
    if (!bottleImages[field]) {
      setError(path, {
        type: 'required',
        message: errorMessage,
      })
      return true
    }
    return false
  })

  return !hasErrors
}

/**
 * Builds array of images for product recognition
 */
export const buildImagesArray = (
  bottleImages: any,
  additionalImages: any
): ProductRecogniseImageInput[] => {
  const images: ProductRecogniseImageInput[] = []

  const bottleImageTypes = [
    { url: bottleImages.frontLabel, type: 'FRONT_LABEL' as const },
    { url: bottleImages.backLabel, type: 'BACK_LABEL' as const },
    { url: bottleImages.bottleLabel, type: 'BOTTLE' as const },
  ]

  bottleImageTypes.forEach(({ url, type }) => {
    if (url) {
      images.push({
        url: url as string,
        type,
      } as ProductRecogniseImageInput)
    }
  })

  const additionalUrls = [
    additionalImages.first,
    additionalImages.second,
    additionalImages.third,
  ]

  additionalUrls.forEach(url => {
    if (url) {
      images.push({
        url: url as string,
        type: undefined,
      } as ProductRecogniseImageInput)
    }
  })

  return images
}

/**
 * Handles async operations with loading state and error handling
 */
export const withLoadingAndError = async <T>(
  operation: () => Promise<T>,
  setIsSubmitting: (value: boolean) => void,
  notify: (message: string, type: 'error' | 'success') => void
): Promise<T | undefined> => {
  try {
    setIsSubmitting(true)
    return await operation()
  } catch (error) {
    if (error instanceof Error) {
      notify(error.message, 'error')
    }
    return undefined
  } finally {
    setIsSubmitting(false)
  }
}

interface CompanyUpdateParams {
  companyId: string
  refetchMe: () => Promise<ApolloQueryResult<any>>
  getTour: (companyId: string) => Promise<any>
  dispatch: any
  setData: any
  setSelectedCompanyId?: any
}

/**
 * Updates user data with new company and tour information
 */
export const updateCompanyInStore = async ({
  companyId: _companyId,
  refetchMe,
  getTour,
  dispatch,
  setData,
  setSelectedCompanyId,
}: CompanyUpdateParams): Promise<boolean> => {
  // Refresh user data to get new company
  const { data: meData } = await refetchMe()
  if (!meData?.me?.companies?.length) {
    return false
  }
  const companyId = _companyId || meData?.me?.companies?.[0]?.id

  // Fetch company tour and update store
  const companyTour = await getTour(companyId)
  const companies = (meData.me.companies ?? []).map((company: any) => {
    if (companyId && company.id === companyId) {
      return { ...company, tour: companyTour }
    }
    return company
  })

  dispatch(setData({ ...meData.me, companies }))

  // Set the newly created company as selected
  if (setSelectedCompanyId) {
    dispatch(setSelectedCompanyId(companyId))
    ls.setItem('wwl-selected_company_id', companyId)
  }

  return true
}

interface OnboardingCompleteResult {
  companyId?: string
  verificationStatusCode?: string
  companyCategory?: string
}

/**
 * Extracts company data from onboarding complete response
 */
export const extractCompanyData = (data: any): OnboardingCompleteResult => {
  const record = data?.userOnboardingComplete?.record
  return {
    companyId: record?.company?.id,
    verificationStatusCode: record?.company?.verificationStatus?.code,
    companyCategory: record?.company?.companyCategory?.masterName,
  }
}
